import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { list, changeGroupName } from "../../../services/packageService";
import SubPackages from "./SubPackages";
import Tooltip from "../../common/Tooltip";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  TableActionsCell,
  ExpandableText,
} from "../../common/adminTable";

import { useState, useMemo, useEffect, useRef } from "react";

const OUTLINE_ACTION_CLASS =
  "inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50";

const FILTER_SELECT_CLASS =
  "min-w-[8.5rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-400 focus:outline-none";

const GROUP_NAME_COLUMN_CLASS = "w-64 max-w-64";

const formatTourType = (type) =>
  type
    ?.split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "—";

const Package = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showToaster, setShowToaster] = useState(false);
  const [viewSubPackages, setViewSubPackages] = useState(false);
  const [packageGroupId, setPackageGroupId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [tourTypeFilter, setTourTypeFilter] = useState("all");
  const [packageCountFilter, setPackageCountFilter] = useState("all");

  const queryClient = useQueryClient();

  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const filters = {
    search: debouncedSearchQuery.trim() || undefined,
    tour_type: tourTypeFilter !== "all" ? tourTypeFilter : undefined,
    package_count:
      packageCountFilter !== "all" ? packageCountFilter : undefined,
  };

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "packageGroup",
      currentPage,
      debouncedSearchQuery,
      tourTypeFilter,
      packageCountFilter,
    ],
    queryFn: () => list(currentPage, filters),
    placeholderData: keepPreviousData,
  });

  const { data: allTourTypesData } = useQuery({
    queryKey: ["packageGroupTourTypes"],
    queryFn: () => list(1, {}),
    enabled: true,
  });

  const uniqueTourTypes = useMemo(() => {
    const types = new Set();
    const dataSource = allTourTypesData?.data?.data?.data || [];
    dataSource.forEach((pkg) => {
      if (pkg.packages?.length > 0 && pkg.packages[0]?.tour_type) {
        types.add(pkg.packages[0].tour_type);
      }
    });
    return Array.from(types);
  }, [allTourTypesData]);

  const pageMeta = SUPPLIER_PAGE_META.packages;

  const packageGroup = response?.data?.data?.data || [];
  const current_page = response?.data?.data?.current_page || 1;
  const last_page = response?.data?.data?.last_page || 1;
  const total = response?.data?.data?.total || 0;
  const per_page = response?.data?.data?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const add = () => {
    navigate("/packages/new");
  };

  const packageHeaderActions = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => navigate("/slots")}
        className={OUTLINE_ACTION_CLASS}
      >
        <i className="fi fi-rr-calendar-check text-sm" />
        Slots
      </button>
      <button
        type="button"
        onClick={() => navigate("/stop-sales")}
        className={OUTLINE_ACTION_CLASS}
      >
        <i className="fi fi-rr-ban text-sm" />
        Stop Sales
      </button>
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
      >
        <i className="fi fi-rr-plus text-sm" />
        Add Package
      </button>
    </div>
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setTourTypeFilter("all");
    setPackageCountFilter("all");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    debouncedSearchQuery.trim() !== "" ||
    tourTypeFilter !== "all" ||
    packageCountFilter !== "all";

  const hasTableRows = packageGroup.some(
    (pkg) => Array.isArray(pkg.packages) && pkg.packages.length > 0
  );

  const view = (id) => {
    setPackageGroupId(id);
    setViewSubPackages(true);
  };

  const addSubPackage = (id, packages, package_count) => {
    const restrictedType = packages[0].tour_type;
    const count =
      package_count ?? packages?.length ?? 0;
    navigate("/packages/new", {
      state: {
        addSubPackageId: id,
        restrictedTourType: restrictedType,
        selectedGroupPackageCount: count,
      },
    });
  };

  const handleNameUpdate = async (id, newName) => {
    if (!newName.trim()) {
      setUpdateError({ id, message: "Group name cannot be empty" });
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError(null);
      await changeGroupName(id, newName);

      await queryClient.invalidateQueries(["packageGroup"]);

      setEditingId(null);
      setShowToaster(true);
    } catch (error) {
      setUpdateError({
        id,
        message: error.response?.data?.message || "Failed to update group name",
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={packageHeaderActions}
      />

      <AdminPageCard>
        <AdminPageCardToolbar className="border-b border-gray-100 bg-white px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search groups..."
                className="w-full sm:max-w-[220px]"
              />
              <select
                value={tourTypeFilter}
                onChange={(e) => {
                  setTourTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Tour type"
              >
                <option value="all">All types</option>
                {uniqueTourTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatTourType(type)}
                  </option>
                ))}
              </select>
              <select
                value={packageCountFilter}
                onChange={(e) => {
                  setPackageCountFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Package count"
              >
                <option value="all">Any count</option>
                <option value="1">1 package</option>
                <option value="2-3">2–3 packages</option>
                <option value="4+">4+ packages</option>
              </select>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                <span className="font-semibold text-gray-900">{total}</span>{" "}
                {total === 1 ? "group" : "groups"}
              </span>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-medium text-gray-600 transition hover:text-gray-900"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </AdminPageCardToolbar>

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-gray-500">
            Loading packages...
          </div>
        ) : error ? (
          <div className="p-6 text-red-600">Error: {error.message}</div>
        ) : (
          <AdminTable
            className="[&_table]:table-fixed"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh className={GROUP_NAME_COLUMN_CLASS}>Group</AdminTableTh>
              <AdminTableTh>Packages</AdminTableTh>
              <AdminTableTh>Type</AdminTableTh>
              <AdminTableTh>Created</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {!hasTableRows ? (
                <TableEmptyRow
                  colSpan={5}
                  {...getTableEmptyProps("packages", {
                    onAdd: add,
                    hasActiveFilters,
                    onClearFilters: clearFilters,
                  })}
                />
              ) : (
                packageGroup.map((pkg) =>
                  pkg.packages.length > 0 ? (
                    <AdminTableRow key={pkg.id}>
                      <AdminTableCell
                        className={`group relative ${GROUP_NAME_COLUMN_CLASS}`}
                        nowrap={false}
                      >
                        {editingId === pkg.id ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={() => {
                                  if (!updateLoading) {
                                    handleNameUpdate(pkg.id, editingName);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleNameUpdate(pkg.id, editingName);
                                  } else if (e.key === "Escape") {
                                    setEditingId(null);
                                    setUpdateError(null);
                                  }
                                }}
                                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/20 ${
                                  updateError?.id === pkg.id
                                    ? "border-red-500"
                                    : "border-gray-200 focus:border-primary"
                                }`}
                                disabled={updateLoading}
                                autoFocus
                              />
                              {updateLoading && editingId === pkg.id && (
                                <i className="fi fi-rr-spinner animate-spin text-gray-500"></i>
                              )}
                            </div>
                            {updateError?.id === pkg.id && (
                              <p className="text-xs text-red-500">
                                {updateError.message}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex min-w-0 items-start gap-2">
                            <ExpandableText
                              text={pkg.name}
                              label="full group name"
                              className="min-w-0 flex-1 font-medium text-gray-900"
                            />
                            <EditActionButton
                              showLabel={false}
                              onClick={() => {
                                setEditingId(pkg.id);
                                setEditingName(pkg.name);
                                setUpdateError(null);
                              }}
                            />
                          </div>
                        )}
                      </AdminTableCell>
                      <AdminTableCell>
                        <span className="text-sm font-medium text-gray-900">
                          {pkg.package_count ??
                            pkg.packages_count ??
                            pkg.packages?.length ??
                            0}
                        </span>
                      </AdminTableCell>
                      <AdminTableCell>
                        {(() => {
                          const tourTypes = new Set(
                            pkg.packages.map((p) => p.tour_type)
                          );

                          if (tourTypes.size > 1) {
                            return (
                              <Tooltip
                                message={`Contains: ${Array.from(tourTypes)
                                  .map((type) => formatTourType(type))
                                  .join(", ")}`}
                                position="top"
                              >
                                <span className="cursor-help text-sm text-gray-600">
                                  Mixed
                                </span>
                              </Tooltip>
                            );
                          }

                          return (
                            <span className="text-sm text-gray-600">
                              {formatTourType(pkg.packages[0]?.tour_type)}
                            </span>
                          );
                        })()}
                      </AdminTableCell>
                      <AdminTableCell className="text-sm text-gray-500">
                        {formatListingDateTime(pkg.created_at_formatted || pkg.created_at)}
                      </AdminTableCell>
                      <TableActionsCell>
                        <button
                          type="button"
                          onClick={() => view(pkg.id)}
                          className="text-sm font-medium text-gray-700 transition hover:text-gray-900"
                        >
                          View
                        </button>
                      </TableActionsCell>
                    </AdminTableRow>
                  ) : null
                )
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      {viewSubPackages && (
        <SubPackages
          open={viewSubPackages}
          onClose={() => setViewSubPackages(false)}
          packageGroupId={packageGroupId}
          groupName={
            packageGroup.find((pkg) => pkg.id === packageGroupId)?.name || ""
          }
          packageCount={
            packageGroup.find((pkg) => pkg.id === packageGroupId)?.package_count ??
            packageGroup.find((pkg) => pkg.id === packageGroupId)?.packages_count ??
            packageGroup.find((pkg) => pkg.id === packageGroupId)?.packages?.length ??
            0
          }
          onAddCombination={() => {
            const group = packageGroup.find((pkg) => pkg.id === packageGroupId);
            if (group) {
              addSubPackage(
                group.id,
                group.packages,
                group.package_count ?? group.packages_count ?? group.packages?.length
              );
              setViewSubPackages(false);
            }
          }}
        />
      )}

      {showToaster && (
        <Toster
          message="Package group name updated successfully!"
          type="success"
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Package;
